import React from 'react'

/**
 * 画像を表示するコンポーネント。`fit` prop でレンダリングモードを切り替える。
 *
 * - `fit='default'`: 指定アスペクト比（`aspectRatio`）の領域に object-contain で表示
 *   - width : height = 25 : 16 => height / width = 480 / 750 = 0.64 が基準
 *   - width : height = 28 : 33 => height / width = 990 / 840 = 1.178... が基準
 * - `fit='cover'`: 指定アスペクト比（`aspectRatio`）の領域を object-cover で埋める。
 *   比が違う画像は中央を基準に切り抜かれる（#3729）
 * - `fit='width-fit'`: 幅全体に引き伸ばし、高さは auto。
 *   `placeholderAspectRatio` を渡すと、画像ロード前・読み込み失敗時だけその比で高さを確保する
 *   （CSS の `aspect-ratio: auto <比>`。ロード後は画像本来の比に従う）
 * - `fit='contain-natural'`: コンテナ（w-full h-full）を埋め、画像の自然なアスペクト比で
 *   object-contain 表示。`aspectRatio` prop は無視される。
 *   ポップアップや任意サイズ画像の表示に使用する。
 */
export const Image = ({
  src,
  alt = '',
  aspectRatio = '25/16',
  fit = 'default',
  placeholderAspectRatio,
}: {
  src: string
  /** 画像の代替テキスト。装飾画像の場合は空文字（デフォルト）を指定する */
  alt?: string
  aspectRatio?: '25/16' | '28/33'
  fit?: 'default' | 'cover' | 'width-fit' | 'contain-natural'
  /**
   * `fit='width-fit'` のときだけ有効。画像ロード前に確保する高さの比（例: '16 / 9'）。
   * ロード後に下の要素が押し下げられて誤タップを招く箇所（ダイアログの選択肢など）で使う
   */
  placeholderAspectRatio?: '16 / 9' | '1 / 1'
}) => {
  if (fit === 'default') {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full w-full h-full object-contain"
          style={{ aspectRatio }}
        />
      </div>
    )
  }
  if (fit === 'cover') {
    return <img src={src} alt={alt} className="block w-full object-cover" style={{ aspectRatio }} />
  }
  if (fit === 'width-fit') {
    return (
      <img
        src={src}
        alt={alt}
        className="w-full h-auto"
        style={placeholderAspectRatio ? { aspectRatio: `auto ${placeholderAspectRatio}` } : undefined}
      />
    )
  }
  if (fit === 'contain-natural') {
    // aspectRatio prop は使用しない。コンテナの w-full h-full に対して object-contain で内接表示する。
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <img src={src} alt={alt} className="block w-full h-full object-contain" />
      </div>
    )
  }
  return null
}
